import React, { useEffect } from "react";
import styles from "./AccessSubPages.module.css";
import { AccessSubPageShell, useScrollToTop } from "./AccessSubPageShell";
import { AnalyticsService } from "../../analytics/AnalyticsService";

/* ============================================================================
   AccessAboutProgram.jsx
   Medicare ACCESS Google Ads subpage — "About the ACCESS Program"

   Content is the program and cost section (S3) of the ACCESS landing page,
   lbpNewLandingPage.jsx, carried verbatim. The only structural change is
   that the section H2 is promoted to the page H1 and the opening paragraph
   moves into the hero.

   Chrome, CTA band and footer come from AccessSubPageShell.
   ============================================================================ */

export const AccessAboutProgram = () => {
  useScrollToTop();

  /* DEV NOTE — analytics is not wired here on purpose. Three things are
     unresolved and should be settled before adding it:
       1. Screen naming across the three subpages (handoff Section 5, item 7).
       2. Whether gclid / variant capture is needed on entry. These are paid
          ads destinations, so the D2C subpages' pattern in HowItWorks.jsx is
          the closest reference.
       3. Storage key behaviour when a visitor moves between subpages
          mid-session (handoff Section 5, item 8).
     Nothing here reads or writes localStorage, so no key is assumed. */
  useEffect(() => {
    // Track page view for the About the ACCESS Program subpage
    const trackingFields = {
      variant: new URLSearchParams(window.location.search).get("variant") || localStorage.getItem("variant") || null,
      isComeFromGoogle: !!(new URLSearchParams(window.location.search).get("gclid") || localStorage.getItem("variant")),
      visitedAccessAboutProgram: true,
      access_subpage: "about_program",
    };

    AnalyticsService.startSession(trackingFields);
    AnalyticsService.trackStep({
      ...trackingFields,
      session_last_update: new Date().toISOString(),
    });
  }, []);

  return (
    <AccessSubPageShell activePage="about">
      {/* ===== HERO ===== */}
      <div className={styles.subhero}>
        <div className={styles.container}>
          <span className={styles.label}>About the program</span>
          <h1>A real Medicare program. Not a subscription.</h1>
          <p>ACCESS stands for Advancing Chronic Care with Effective, Scalable Solutions. It is a payment model created by the Centers for Medicare and Medicaid Services to pay for care delivered at home using technology.</p>
        </div>
      </div>

      {/* ===== BODY — landing page S3 ===== */}
      <section className={styles.program}>
        <div className={styles.container}>
          <p>Virdio Health was accepted into the CMS ACCESS Model through a formal application process that includes a licensed medical director, credentialed physical therapists, and CMS integration testing. As an approved participating provider, Medicare pays us directly for your care, the same way it pays a clinic or a hospital.</p>

          <p>
            You can read about the program on Medicare's own website at
            <a href="https://www.medicare.gov/access" target="_blank" rel="noopener noreferrer">
              <strong> Medicare.gov/ACCESS</strong>
            </a>
            , and you can find Virdio Health on the
            <a href="https://www.cms.gov/priorities/innovation/access-model-accepted-applicants" target="_blank" rel="noopener noreferrer">
              <strong> CMS list of accepted ACCESS participants</strong>
            </a>
            .
          </p>

          <div className={styles.costcard}>
            <h3>What it costs you</h3>
            <p className={styles.big}>
              <strong>Nothing.</strong> Medicare pays Virdio Health directly, and Virdio Health covers the share you would normally owe.
            </p>
            <ul>
              <li>
                <strong>No copay and no deductible.</strong> You will not be billed for this program.
              </li>
              <li>
                <strong>Nothing to buy.</strong> No subscription, no equipment to purchase, nothing to cancel.
              </li>
              <li>
                <strong>Nothing to owe afterwards.</strong> There is no bill at the end.
              </li>
              <li>
                <strong>Your Medicare does not change.</strong> Taking part does not affect your Medicare benefits, your rights, or your coverage. You can still see any Medicare provider you choose.
              </li>
            </ul>
          </div>
        </div>
      </section>
    </AccessSubPageShell>
  );
};

export default AccessAboutProgram;
